import { mixed, object, ref, string } from 'yup';
import { IntlMessage } from 'common/hooks/useFormatMessage';
import { validatePesel } from 'common/config/validatePesel';
import { PersonalDocument } from 'common/components/PersonalDocumentTypeSelect';

export const emailRegex = /[a-zA-Z0-9+._%-\\+]{1,256}@[a-zA-Z0-9.\\-]{0,64}(.[a-zA-Z]{1,32}$)/;

export const partnerNameValidationSchema = (intlMessage: IntlMessage) =>
  string()
    .required(
      intlMessage(
        { id: 'validation.required' },
        {
          field: intlMessage({ id: 'name' }),
        }
      )
    )
    .max(
      200,
      intlMessage(
        { id: 'validation.maxLength' },
        {
          field: intlMessage({ id: 'name' }),
          length: 200,
        }
      )
    );

export const partnerUniqueNumberValidationSchema = (intlMessage: IntlMessage) =>
  string().max(
    100,
    intlMessage(
      { id: 'validation.maxLength' },
      {
        field: intlMessage({ id: 'number' }),
        length: 100,
      }
    )
  );

export const emailValidationSchema = (intlMessage: IntlMessage) =>
  string()
    .email(
      intlMessage(
        { id: 'validation.valid' },
        {
          field: 'email',
        }
      )
    )
    .matches(
      emailRegex,
      intlMessage(
        { id: 'validation.valid' },
        {
          field: 'email',
        }
      )
    )
    .max(
      100,
      intlMessage(
        { id: 'validation.maxLength' },
        {
          field: 'email',
          length: 100,
        }
      )
    )
    .nullable();

export const requiredEmailValidationSchema = (intlMessage: IntlMessage) =>
  emailValidationSchema(intlMessage).required(
    intlMessage(
      { id: 'validation.required' },
      {
        field: 'email',
      }
    )
  );

const hasWhiteSpaceAtStart = /[^\s]+/;
const minTwoLetters = /^[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF\u0400-\u04FF\s-]{2,}$/;
const onlyNumberRegex = /^[0-9\s]*$/;

export const firstNameValidationSchema = (intlMessage: IntlMessage) =>
  string()
    .max(
      50,
      intlMessage(
        { id: 'validation.maxLength' },
        {
          field: intlMessage({ id: 'person.firstName' }),
          length: 50,
        }
      )
    )
    .required(
      intlMessage(
        { id: 'validation.required' },
        {
          field: intlMessage({ id: 'person.firstName' }),
        }
      )
    )
    .matches(
      hasWhiteSpaceAtStart,
      intlMessage(
        { id: 'validation.required' },
        {
          field: intlMessage({ id: 'person.firstName' }),
        }
      )
    );

export const lastNameValidationSchema = (intlMessage: IntlMessage) =>
  string()
    .max(
      50,
      intlMessage(
        { id: 'validation.maxLength' },
        {
          field: intlMessage({ id: 'person.lastName' }),
          length: 50,
        }
      )
    )
    .required(
      intlMessage(
        { id: 'validation.required' },
        {
          field: intlMessage({ id: 'person.lastName' }),
        }
      )
    )
    .matches(minTwoLetters, intlMessage({ id: 'validation.minimumTwoLetters' }))
    .matches(
      hasWhiteSpaceAtStart,
      intlMessage(
        { id: 'validation.required' },
        {
          field: intlMessage({ id: 'person.lastName' }),
        }
      )
    );

export const phoneNumberPrefixCodeValidation = (intlMessage: IntlMessage) =>
  string()
    .nullable()
    .when('phoneNumber', {
      is: (val) => !!val,
      then: string().required(
        intlMessage(
          { id: 'validation.required' },
          {
            field: intlMessage({ id: 'person.phoneNumber.prefixCode' }),
          }
        )
      ),
      otherwise: string(),
    })
    .matches(
      onlyNumberRegex,
      intlMessage(
        { id: 'validation.valid' },
        {
          field: intlMessage({ id: 'person.phoneNumber.prefixCode' }),
        }
      )
    )
    .max(
      3,
      intlMessage(
        { id: 'validation.maxLength' },
        {
          field: intlMessage({ id: 'person.phoneNumber.prefixCode' }),
          length: 3,
        }
      )
    );

export const phoneNumberValidationSchema = (intlMessage: IntlMessage) =>
  string()
    .nullable()
    .max(
      100,
      intlMessage(
        { id: 'validation.maxLength' },
        {
          field: intlMessage({ id: 'person.phoneNumber' }),
          length: 100,
        }
      )
    )
    .matches(
      onlyNumberRegex,
      intlMessage(
        { id: 'validation.valid' },
        {
          field: intlMessage({ id: 'person.phoneNumber' }),
        }
      )
    )
    .when('prefixCode', {
      is: (val) => !!val,
      then: string().required(
        intlMessage(
          { id: 'validation.required' },
          {
            field: intlMessage({ id: 'person.phoneNumber' }),
          }
        )
      ),
      otherwise: string(),
    });

export const genderValidationSchema = (intlMessage: IntlMessage) =>
  string().required(
    intlMessage(
      { id: 'validation.required' },
      {
        field: intlMessage({ id: 'person.gender' }),
      }
    )
  );

export const medicalSpecializationValidationSchema = (
  intlMessage: IntlMessage
) =>
  string().max(
    255,
    intlMessage(
      { id: 'validation.maxLength' },
      {
        field: intlMessage({ id: 'person.medicalSpecialization' }),
        length: 255,
      }
    )
  );

export const jobTitleValidationSchema = (intlMessage: IntlMessage) =>
  string().max(
    100,
    intlMessage(
      { id: 'validation.maxLength' },
      {
        field: intlMessage({ id: 'person.jobTitle' }),
        length: 100,
      }
    )
  );

const passwordRegex = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^?!():])(?=\S+$).{8,}$/;

export const newPasswordValidationSchema = (intlMessage: IntlMessage) =>
  object().shape({
    oldPassword: string().required(
      intlMessage(
        { id: 'validation.required' },
        {
          field: intlMessage({ id: 'person.password.current' }),
        }
      )
    ),
    newPassword: string()
      .required(
        intlMessage(
          { id: 'validation.required' },
          {
            field: intlMessage({ id: 'person.password.new' }),
          }
        )
      )
      .matches(passwordRegex, intlMessage({ id: 'validation.passwordRegex' })),
    repeatNewPassword: string()
      .required(intlMessage({ id: 'validation.passwordConfirm' }))
      .oneOf(
        [ref('newPassword')],
        intlMessage({ id: 'validation.passwordMatch' })
      ),
  });

export const dateOfBirthValidationSchema = (intlMessage: IntlMessage) =>
  string()
    .required(
      intlMessage(
        { id: 'validation.required' },
        {
          field: intlMessage({ id: 'person.dateOfBirth' }),
        }
      )
    )
    .nullable();

export const twoFactorPinCodeValidationSchema = (intlMessage: IntlMessage) =>
  object().shape({
    mfaVerificationCode: string().required(
      intlMessage(
        { id: 'validation.required' },
        {
          field: intlMessage({ id: 'pinCode' }),
        }
      )
    ),
  });

export const idNumberValidationSchema = (intlMessage: IntlMessage) =>
  mixed()
    .when('idNumberDocumentType', {
      is: (v) =>
        v === PersonalDocument.Pesel ||
        v === PersonalDocument.ResidentCard ||
        v === PersonalDocument.Passport,
      then: string().max(
        50,
        intlMessage(
          { id: 'validation.maxLength' },
          {
            field: intlMessage({ id: 'person.idNumber' }),
            length: 50,
          }
        )
      ),
    })

    .when('idNumberDocumentType', {
      is: (v) => v === PersonalDocument.Pesel,
      then: string().test(
        'pesel',
        intlMessage(
          { id: 'validation.valid' },
          {
            field: intlMessage({ id: 'person.personalId' }),
            length: 50,
          }
        ),
        (value) => validatePesel(value)
      ),
    })
    .when('idNumberDocumentType', {
      is: (v) =>
        v === PersonalDocument.Pesel ||
        v === PersonalDocument.Passport ||
        v === PersonalDocument.ResidentCard,
      then: string().required(
        intlMessage(
          { id: 'validation.required' },
          {
            field: intlMessage({ id: 'person.idNumber' }),
          }
        )
      ),
    });
